import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

export interface TypeaheadOption {
  value: string | number;
  label: string;
  [key: string]: any;
}

export interface TypeaheadProps {
  options: TypeaheadOption[];
  value?: string;
  onChange: (value: string) => void;
  onSelect?: (option: TypeaheadOption) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  dropdownClassName?: string;
  loading?: boolean;
  minChars?: number;
  maxResults?: number;
  onClose?: () => void;
  renderOption?: (option: TypeaheadOption) => React.ReactNode;
}

const Typeahead: React.FC<TypeaheadProps> = ({
  options,
  value = '',
  onChange,
  onSelect,
  placeholder = 'Search...',
  className = '',
  inputClassName = '',
  dropdownClassName = '',
  loading = false,
  minChars = 1,
  maxResults = 10,
  renderOption,
  onClose,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = useMemo(
    () => (value.length >= minChars ? options.slice(0, maxResults) : []),
    [value, minChars, options, maxResults],
  );
  const showDropdown = isOpen && (filteredOptions.length > 0 || loading);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      onChange(newValue);
      setIsOpen(true);
      setHighlightedIndex(-1);
    },
    [onChange],
  );

  const handleOptionSelect = useCallback(
    (option: TypeaheadOption) => {
      onChange(option.label);
      onSelect?.(option);
      setIsOpen(false);
      setHighlightedIndex(-1);
    },
    [onChange, onSelect],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showDropdown) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(prev => (prev < filteredOptions.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
            handleOptionSelect(filteredOptions[highlightedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;
      }
    },
    [showDropdown, filteredOptions, highlightedIndex, handleOptionSelect],
  );

  const defaultRenderOption = (option: TypeaheadOption) => (
    <div className="flex items-center">
      <span>{option.label}</span>
    </div>
  );

  return (
    <div className={classNames('relative', className)} ref={dropdownRef}>
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className={classNames(
            'w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
            inputClassName,
          )}
        />
      </div>

      {showDropdown && (
        <div
          className={classNames(
            'absolute z-[100] mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto',
            dropdownClassName,
          )}
        >
          {loading ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">Loading...</div>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleOptionSelect(option)}
                className={classNames(
                  'w-full text-left px-4 py-2 text-sm hover:bg-gray-100 focus:outline-none focus:bg-gray-100',
                  highlightedIndex === index && 'bg-gray-100',
                )}
              >
                {renderOption ? renderOption(option) : defaultRenderOption(option)}
              </button>
            ))
          ) : (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">No results found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default Typeahead;
